/** @format */

import * as Sentry from "@sentry/react";
import { ReadyState } from "react-use-websocket";

import { trackEvent } from "@/utils/analytics";

type MessageHandler = (message: MessageEvent, runID: string) => void;

interface PlaybackPosition {
  lap?: number;
  lapStart?: number;
  lapEnd?: number;
  lapProgress?: number;
  timestamp?: number;
}

interface ConnectionInfo {
  readyState: ReadyState;
  subscribers: Set<MessageHandler>;
  sendJsonMessage: ((message: Record<string, unknown>) => void) | null;
  playbackPosition: PlaybackPosition | null;
  subscriberCount: number;
  /** When the socket last reached OPEN; null while not open. */
  openedAt: number | null;
  /** Connect attempts that closed without ever opening, since the last open. */
  failedAttempts: number;
  primaryClaimed: boolean;
  pendingRemoval: ReturnType<typeof setTimeout> | null;
}

class WebSocketManager {
  private connections: Map<string, ConnectionInfo> = new Map();

  private createConnection(runID: string, primaryClaimed: boolean): ConnectionInfo {
    const conn: ConnectionInfo = {
      readyState: ReadyState.CONNECTING,
      subscribers: new Set(),
      sendJsonMessage: null,
      playbackPosition: null,
      subscriberCount: 0,
      openedAt: null,
      failedAttempts: 0,
      primaryClaimed,
      pendingRemoval: null,
    };
    this.connections.set(runID, conn);
    return conn;
  }

  /**
   * Atomically claim primary connection status for a runID
   * Returns true if this call successfully claimed primary status, false if already claimed.
   * An entry whose primary has been released (e.g. rapid unmount/remount) is reclaimed,
   * preserving any existing subscribers.
   */
  claimPrimary(runID: string): boolean {
    const existing = this.connections.get(runID);
    if (existing) {
      // Cancel any deferred removal scheduled by releasePrimary — the new
      // claimant takes over the entry and its subscribers.
      if (existing.pendingRemoval !== null) {
        clearTimeout(existing.pendingRemoval);
        existing.pendingRemoval = null;
      }
      if (existing.primaryClaimed) {
        return false;
      }
      existing.primaryClaimed = true;
      existing.readyState = ReadyState.CONNECTING;
      existing.openedAt = null;
      existing.failedAttempts = 0;

      Sentry.addBreadcrumb({
        category: "websocket",
        message: `WebSocket primary reclaimed`,
        data: { runID },
        level: "info",
      });

      return true;
    }

    this.createConnection(runID, true);

    // Track connection event in Sentry
    Sentry.addBreadcrumb({
      category: "websocket",
      message: `WebSocket connecting`,
      data: { runID },
      level: "info",
    });

    return true;
  }

  /**
   * Release primary ownership for a runID (called when the primary unmounts).
   * The entry is kept for a grace period so a quickly remounting component can
   * reclaim it; afterwards it is removed only if still unclaimed and idle.
   */
  releasePrimary(runID: string, graceMs = 1000): void {
    const conn = this.connections.get(runID);
    if (!conn) {
      return;
    }

    conn.primaryClaimed = false;
    conn.sendJsonMessage = null;
    if (conn.pendingRemoval !== null) {
      clearTimeout(conn.pendingRemoval);
    }
    conn.pendingRemoval = setTimeout(() => {
      const current = this.connections.get(runID);
      if (!current) {
        return;
      }
      current.pendingRemoval = null;
      if (!current.primaryClaimed && current.subscribers.size === 0) {
        this.connections.delete(runID);
      }
    }, graceMs);
  }

  /**
   * Register a connection for a runID (must be called after claimPrimary)
   */
  registerConnection(
    runID: string,
    sendJsonMessage: (message: Record<string, unknown>) => void,
    readyState: ReadyState
  ): void {
    const conn = this.connections.get(runID);
    if (conn) {
      conn.sendJsonMessage = sendJsonMessage;
      conn.readyState = readyState;
    }
  }

  /**
   * Update connection state and track state changes
   */
  updateConnectionState(runID: string, readyState: ReadyState): void {
    const conn = this.connections.get(runID);
    if (!conn) {
      return;
    }

    const previousState = conn.readyState;
    conn.readyState = readyState;

    // Track state change in Sentry
    if (previousState !== readyState) {
      const stateNames: Record<ReadyState, string> = {
        [ReadyState.CONNECTING]: "connecting",
        [ReadyState.OPEN]: "open",
        [ReadyState.CLOSING]: "closing",
        [ReadyState.CLOSED]: "closed",
        [ReadyState.UNINSTANTIATED]: "uninstantiated",
      };

      const previousStateName = stateNames[previousState] || "unknown";
      const currentStateName = stateNames[readyState] || "unknown";

      Sentry.addBreadcrumb({
        category: "websocket",
        message: `WebSocket state change: ${previousStateName} → ${currentStateName}`,
        data: {
          runID,
          previousState: previousStateName,
          currentState: currentStateName,
        },
        level: "info",
      });

      // Track successful connection
      if (readyState === ReadyState.OPEN && previousState !== ReadyState.OPEN) {
        Sentry.addBreadcrumb({
          category: "websocket",
          message: `WebSocket connected`,
          data: { runID },
          level: "info",
        });
        conn.openedAt = Date.now();
        // Failed attempts are reported here, once per outage, rather than
        // each one as a "disconnect" — a 1 s retry loop during an outage used
        // to emit one disconnect per second per tab.
        trackEvent("websocket_connected", {
          run_id: runID,
          failed_attempts: conn.failedAttempts,
        });
        conn.failedAttempts = 0;
      }

      // Track disconnection and record duration
      if (readyState === ReadyState.CLOSED && previousState !== ReadyState.CLOSED) {
        if (conn.openedAt === null) {
          // CONNECTING → CLOSED: the attempt never opened, so there is no
          // connection to have lost.
          conn.failedAttempts += 1;
        } else {
          const duration = Date.now() - conn.openedAt;
          conn.openedAt = null;

          // Static measurement name: this was
          // `websocket.${runID}.connection_duration`, which mints one custom
          // measurement key per run UUID and blows Sentry's per-org limit. The
          // run is carried on the breadcrumb below instead.
          //
          // Likewise no Sentry.setTag here — that writes to the *global*
          // scope, so `websocket_run_id` then rode along on every unrelated
          // event for the rest of the session.
          Sentry.setMeasurement(
            "websocket.connection_duration",
            duration,
            "millisecond"
          );

          Sentry.addBreadcrumb({
            category: "websocket",
            message: `WebSocket disconnected`,
            data: { runID, duration },
            level: "info",
          });

          trackEvent("websocket_disconnected", {
            run_id: runID,
            duration_seconds: Math.round(duration / 1000),
          });
        }
      }
    }
  }

  /**
   * Subscribe to messages for a given runID. If no connection entry exists
   * yet (subscriber mounted before the primary), one is created so the
   * handler is attached when the primary arrives.
   */
  subscribe(runID: string, handler: MessageHandler): () => void {
    const conn = this.connections.get(runID) ?? this.createConnection(runID, false);

    conn.subscribers.add(handler);
    conn.subscriberCount = conn.subscribers.size;

    // Return unsubscribe function
    return () => {
      conn.subscribers.delete(handler);
      conn.subscriberCount = conn.subscribers.size;
      // Last subscriber gone and no primary: drop the idle entry
      if (conn.subscribers.size === 0 && !conn.primaryClaimed) {
        if (conn.pendingRemoval !== null) {
          clearTimeout(conn.pendingRemoval);
          conn.pendingRemoval = null;
        }
        if (this.connections.get(runID) === conn) {
          this.connections.delete(runID);
        }
      }
    };
  }

  /**
   * Broadcast a message to all subscribers for a runID
   */
  broadcastMessage(runID: string, message: MessageEvent): void {
    const conn = this.connections.get(runID);
    if (!conn) {
      return;
    }

    conn.subscribers.forEach(handler => {
      try {
        handler(message, runID);
      } catch (error) {
        Sentry.captureException(
          error instanceof Error ? error : new Error(String(error))
        );
      }
    });
  }

  /**
   * Start playback for a runID at a target lap
   */
  startPlayback(runID: string, targetLap: number): void {
    const conn = this.connections.get(runID);
    if (!conn) {
      return;
    }

    // Send playback_start message if connected
    if (conn.readyState === ReadyState.OPEN && conn.sendJsonMessage) {
      conn.sendJsonMessage({
        type: "playback_start",
        run_id: runID,
        lap: targetLap,
      });
    }
  }

  /**
   * Update playback position for a runID
   */
  updatePlaybackPosition(runID: string, position: PlaybackPosition): void {
    const conn = this.connections.get(runID);
    if (!conn) {
      return;
    }

    // Merge with existing position
    conn.playbackPosition = {
      ...conn.playbackPosition,
      ...position,
    };

    // Send update if connected
    if (conn.readyState === ReadyState.OPEN && conn.sendJsonMessage) {
      conn.sendJsonMessage({
        type: "playback_position_update",
        run_id: runID,
        lap: conn.playbackPosition.lap,
        lap_start: conn.playbackPosition.lapStart,
        lap_end: conn.playbackPosition.lapEnd,
        lap_progress: conn.playbackPosition.lapProgress,
        timestamp: conn.playbackPosition.timestamp,
      });
    }
  }

  /**
   * Get connection info for a runID
   */
  getConnectionInfo(runID: string): ConnectionInfo | null {
    return this.connections.get(runID) ?? null;
  }

  /**
   * Check if a connection exists for a runID
   */
  hasConnection(runID: string): boolean {
    return this.connections.has(runID);
  }

  /**
   * Get the number of active connections
   */
  getConnectionCount(): number {
    return this.connections.size;
  }

  /**
   * Remove a connection (called when no longer needed)
   */
  removeConnection(runID: string): void {
    const conn = this.connections.get(runID);
    if (conn?.pendingRemoval != null) {
      clearTimeout(conn.pendingRemoval);
    }
    this.connections.delete(runID);
  }

  /**
   * Reset all connections. For test use only.
   */
  reset(): void {
    this.connections.forEach(conn => {
      if (conn.pendingRemoval !== null) {
        clearTimeout(conn.pendingRemoval);
      }
    });
    this.connections.clear();
  }
}

// Export singleton instance
export const websocketManager = new WebSocketManager();
